import api from "src/services/api";
import { IVisita, IListarVisitasResponse } from "src/services/visitas";
import { RegistroHistorialAPI, RegistroReclamoAPI } from "src/utils/interfaces";

export interface KpisInicioResponse {
  fecha: string;
  visitas: {
    total: number;
    pendientes: number;
    completadas: number;
    vencidas: number;
  };
  sin_a: {
    total: number;
    por_ps: Array<{
      ps_codigo?: string | null;
      ps_nombre?: string | null;
      cantidad: number;
    }>;
  };
  ausencias?: {
    total: number;
  };
  reclamos: {
    total: number;
  };
}

export const obtenerKpisInicio = async (fecha?: string): Promise<KpisInicioResponse> => {
  const params = fecha ? { fecha } : undefined;
  const { data } = await api.get<KpisInicioResponse>("inicio/kpis", { params });
  return data;
};

export const obtenerDetalleVisitas = async (
  fecha: string,
  fiscalId?: string | number
): Promise<IVisita[]> => {
  const params: { desde: string; hasta: string; fiscal_id?: string | number } = {
    desde: fecha,
    hasta: fecha,
  };
  if (fiscalId) {
    params.fiscal_id = fiscalId;
  }

  const { data } = await api.get<IListarVisitasResponse>(
    "visitas_fiscales/listar",
    { params }
  );
  return data.datos || [];
};

export const obtenerDetalleSinA = async (fecha: string): Promise<RegistroHistorialAPI[]> => {
  const { data } = await api.get<{ datos: RegistroHistorialAPI[] }>(
    "horas/pendientes/",
    { params: { fecha_desde: fecha, fecha_hasta: fecha } }
  );
  return data?.datos || [];
};

export const obtenerDetalleReclamos = async (
  fecha: string
): Promise<RegistroReclamoAPI[]> => {
  const { data } = await api.get<{ datos: RegistroReclamoAPI[] }>(
    "horas/reclamos/todos",
    { params: { fecha_desde: fecha, fecha_hasta: fecha } }
  );
  return data?.datos || [];
};

export interface AusenciaDetalle {
  legajo?: string | number | null;
  operario?: string | null;
  ps_nombre?: string | null;
  ps_codigo?: string | null;
  fecha?: string | null;
  tipo_ausencia?: "asignacion" | "total";
  hora_ent?: string | null;
  hora_sal?: string | null;
}

export interface EncuestasBajasResponse {
  desde: string;
  hasta: string;
  conteos: {
    limpieza: number;
    colaboradores: number;
    equipos: number;
    productos: number;
    fiscal: number;
  };
  datos: EncuestaBajaItem[];
}

export interface EncuestaBajaItem {
  caso_id: number;
  ps_nombre?: string | null;
  ps_codigo?: string | null;
  fecha?: string | null;
  estado?: string | null;
  fiscal_nombre?: string | null;
  colaboradores: number | null;
  limpieza: number | null;
  equipos: number | null;
  productos: number | null;
  fiscal: number | null;
}

export interface ReclamoClienteItem {
  caso_id: number;
  ps_nombre?: string | null;
  ps_codigo?: string | null;
  fecha?: string | null;
  estado?: string | null;
  clasificacion?: string | null;
  gravedad?: string | null;
  conforme?: string | null;
  descripcion?: string | null;
}

export interface ReclamosClientesResponse {
  desde: string;
  hasta: string;
  total: number;
  datos: ReclamoClienteItem[];
}

export interface VacacionesSaldoAnual {
  periodo: number;
  saldo: number;
}

export interface VacacionesDetalle {
  legajo: string;
  nombre_empleado: string;
  fiscal: string;
  fecha_inicio: string;
  proximo_aniversario: string | null;
  dias_faltantes: number | null;
  antiguedad: string;
  antiguedad_decimal: number;
  saldos_anuales: VacacionesSaldoAnual[];
  saldos_pendientes_anteriores: number;
  total: number;
  proximo: boolean;
  vencido: boolean;
}

export interface VacacionesInicioResponse {
  fecha: string;
  dias_alerta: number;
  resumen: {
    proximos: number;
    vencidos: number;
    total: number;
  };
  datos: VacacionesDetalle[];
}

export type AlertaLimpiezaOrigen =
  | "fiscalizacion_limpieza"
  | "evaluacion_visita"
  | "visita_comercial";

export interface AlertaLimpiezaGeneralItem {
  origen?: AlertaLimpiezaOrigen | null;
  fiscalizacion_id?: number | null;
  evaluacion_id?: number | null;
  visita_id?: number | null;
  ps_id?: number | null;
  ps_codigo?: string | null;
  ps_nombre?: string | null;
  calificacion?: string | null;
  fecha?: string | null;
  fiscal_cedula?: string | null;
  fiscal_nombre?: string | null;
  visitado_por?: string | null;
  visitado_por_cedula?: string | null;
  cargo_visitador?: string | null;
  requiere_visita?: boolean;
  mensaje?: string | null;
}

export interface AlertasLimpiezaGeneralResponse {
  desde: string;
  hasta: string;
  total: number;
  datos: AlertaLimpiezaGeneralItem[];
}

export const obtenerDetalleAusencias = async (
  params: { fecha?: string; desde?: string; hasta?: string } | string
): Promise<AusenciaDetalle[]> => {
  const query =
    typeof params === "string"
      ? { fecha: params }
      : {
          fecha: params.fecha,
          desde: params.desde,
          hasta: params.hasta,
        };
  const { data } = await api.get<{ datos: AusenciaDetalle[] }>(
    "inicio/ausencias",
    { params: query }
  );
  return data?.datos || [];
};

export const obtenerEncuestasBajas = async (): Promise<EncuestasBajasResponse> => {
  const { data } = await api.get<EncuestasBajasResponse>("inicio/encuestas-bajas");
  return data;
};

export const obtenerReclamosClientes = async (): Promise<ReclamosClientesResponse> => {
  const { data } = await api.get<ReclamosClientesResponse>("inicio/reclamos-clientes");
  return data;
};

export const obtenerVacacionesInicio = async (
  dias?: number
): Promise<VacacionesInicioResponse> => {
  const params = dias ? { dias } : undefined;
  const { data } = await api.get<VacacionesInicioResponse>("inicio/vacaciones", { params });
  return data;
};

export const obtenerAlertasLimpiezaGeneral =
  async (): Promise<AlertasLimpiezaGeneralResponse> => {
    const { data } = await api.get<AlertasLimpiezaGeneralResponse>(
      "inicio/alertas-limpieza-general"
    );
    return data;
  };

export interface OrdenPendienteItem {
  id: number | null;
  fecha: string | null;
  observacion: string;
  tipo_orden_id: number | null;
  tipo_orden: string;
  ps_codigo: string | null;
  ps_nombre: string | null;
}

export interface OrdenesPendientesResponse {
  total: number;
  datos: OrdenPendienteItem[];
}

export const obtenerOrdenesPendientes =
  async (): Promise<OrdenesPendientesResponse> => {
    const { data } = await api.get<OrdenesPendientesResponse>(
      "inicio/ordenes-pendientes"
    );
    return data;
  };

export const decidirOrdenPendiente = async (
  ordenId: number,
  accion: "aprobar" | "rechazar"
): Promise<{ info: string; orden_id: number; estado: string }> => {
  const { data } = await api.post<{ info: string; orden_id: number; estado: string }>(
    `inicio/ordenes-pendientes/${ordenId}/decidir`,
    { accion }
  );
  return data;
};

export default {
  obtenerKpisInicio,
  obtenerDetalleVisitas,
  obtenerDetalleSinA,
  obtenerDetalleAusencias,
  obtenerEncuestasBajas,
  obtenerReclamosClientes,
  obtenerDetalleReclamos,
  obtenerVacacionesInicio,
  obtenerAlertasLimpiezaGeneral,
  obtenerOrdenesPendientes,
  decidirOrdenPendiente,
};
